// Web talking-head bridge: pipes the deity's remote voice track (PCM16 mono 16 kHz)
// to a local lip-sync renderer over WS, and receives synced video+audio over WebRTC.
export const AVATAR_BASE: string = process.env.EXPO_PUBLIC_AVATAR_URL || 'http://localhost:8010';

const TARGET_RATE = 16000;
const CHUNK = 1600; // ~100 ms

const WORKLET_SRC = `
class Tap extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor('avatar-tap', Tap);
`;

export class AvatarBridge {
  onClosed: (() => void) | null = null;
  private pc: RTCPeerConnection | null = null;
  private ws: WebSocket | null = null;
  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private src: MediaStreamAudioSourceNode | null = null;
  private sink: GainNode | null = null;
  private gen = 0;
  // resampler / chunker state
  private ratio = 1; // source samples per target sample
  private pos = 0; // fractional read position into `carry`
  private carry = new Float32Array(0);
  private out = new Int16Array(CHUNK);
  private outLen = 0;

  static async probe(base: string = AVATAR_BASE): Promise<boolean> {
    try {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 1500);
      const r = await fetch(`${base}/health`, { signal: ctl.signal });
      clearTimeout(timer);
      if (!r.ok) return false;
      const j = (await r.json()) as { ok?: boolean };
      return j.ok === true;
    } catch {
      return false;
    }
  }

  async connect(remoteAudioTrack: MediaStreamTrack, base: string = AVATAR_BASE): Promise<{ videoStream: MediaStream }> {
    this.disconnect();
    const gen = ++this.gen;
    const stale = () => gen !== this.gen;
    try {
      // 1. WebRTC offer/answer with the renderer.
      const pc = new RTCPeerConnection();
      this.pc = pc;
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });
      const remote = new MediaStream();
      const gotVideo = new Promise<MediaStream>((resolve) => {
        pc.ontrack = (e) => {
          remote.addTrack(e.track);
          if (e.track.kind === 'video') resolve(remote);
        };
      });
      pc.onconnectionstatechange = () => {
        if (stale()) return;
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed' || pc.connectionState === 'disconnected') this.onClosed?.();
      };
      await pc.setLocalDescription(await pc.createOffer());
      const res = await fetch(`${base}/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sdp: pc.localDescription!.sdp, type: pc.localDescription!.type }),
      });
      if (!res.ok) throw new Error(`Avatar offer failed (${res.status})`);
      const ans = (await res.json()) as { sdp: string; type: RTCSdpType; sessionid: string | number };
      if (stale()) throw new Error('stale');
      await pc.setRemoteDescription({ sdp: ans.sdp, type: ans.type });

      // 2. Audio WebSocket.
      const wsUrl = `${base.replace(/^http/, 'ws')}/audio?sessionid=${encodeURIComponent(String(ans.sessionid))}`;
      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;
      ws.onclose = () => {
        if (!stale()) this.onClosed?.();
      };
      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve();
        ws.onerror = () => reject(new Error('Avatar audio socket failed'));
      });
      if (stale()) throw new Error('stale');

      // 3. Tap the remote voice track.
      let ctx: AudioContext;
      try {
        ctx = new AudioContext({ sampleRate: TARGET_RATE });
      } catch {
        ctx = new AudioContext();
      }
      this.ctx = ctx;
      this.ratio = ctx.sampleRate / TARGET_RATE;
      this.pos = 0;
      this.carry = new Float32Array(0);
      this.outLen = 0;
      const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
      try {
        await ctx.audioWorklet.addModule(url);
      } finally {
        URL.revokeObjectURL(url);
      }
      if (stale()) throw new Error('stale');
      const node = new AudioWorkletNode(ctx, 'avatar-tap', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1, channelCountMode: 'explicit' });
      node.port.onmessage = (m) => this.push(m.data as Float32Array);
      const src = ctx.createMediaStreamSource(new MediaStream([remoteAudioTrack]));
      const sink = ctx.createGain();
      sink.gain.value = 0; // keeps the graph pulled in Chrome without making sound
      src.connect(node);
      node.connect(sink);
      sink.connect(ctx.destination);
      this.node = node;
      this.src = src;
      this.sink = sink;
      if (ctx.state === 'suspended') await ctx.resume().catch(() => {});

      const videoStream = await Promise.race([
        gotVideo,
        new Promise<never>((_, rej) => setTimeout(() => rej(new Error('Avatar video timed out')), 60000)),
      ]);
      if (stale()) throw new Error('stale');
      return { videoStream };
    } catch (e) {
      if (gen === this.gen) this.disconnect();
      throw e;
    }
  }

  private push(frame: Float32Array): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    let buf = frame;
    if (this.carry.length) {
      buf = new Float32Array(this.carry.length + frame.length);
      buf.set(this.carry);
      buf.set(frame, this.carry.length);
    }
    // Linear-interpolation resample to 16 kHz (ratio === 1 passes straight through).
    let p = this.pos;
    while (p + 1 < buf.length || (this.ratio === 1 && p < buf.length)) {
      let s: number;
      if (this.ratio === 1) s = buf[p | 0];
      else {
        const i = Math.floor(p);
        const f = p - i;
        s = buf[i] * (1 - f) + buf[i + 1] * f;
      }
      const c = s < -1 ? -1 : s > 1 ? 1 : s;
      this.out[this.outLen++] = c < 0 ? c * 0x8000 : c * 0x7fff;
      if (this.outLen === CHUNK) {
        ws.send(this.out.slice().buffer);
        this.outLen = 0;
      }
      p += this.ratio;
    }
    const keep = Math.floor(p);
    this.carry = buf.slice(keep);
    this.pos = p - keep;
  }

  flush(): void {
    this.outLen = 0;
    this.carry = new Float32Array(0);
    this.pos = 0;
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'flush' }));
  }

  disconnect(): void {
    this.gen++;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      try {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'end' }));
        ws.close();
      } catch {
        // ignore
      }
    }
    if (this.node) this.node.port.onmessage = null;
    try { this.src?.disconnect(); } catch { /* ignore */ }
    try { this.node?.disconnect(); } catch { /* ignore */ }
    try { this.sink?.disconnect(); } catch { /* ignore */ }
    this.src = this.node = this.sink = null;
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
    if (this.pc) {
      this.pc.onconnectionstatechange = null;
      this.pc.close();
      this.pc = null;
    }
    this.outLen = 0;
    this.carry = new Float32Array(0);
    this.pos = 0;
  }
}
